import { useUkAwareText } from '@capsicum/socios-core';
import { useMemo } from 'react';

import { WCPledgeTutorialSteps } from '../models';

import { useWCPledgeBonusPercentage } from './useWCPledgeBonusPercentage';

import type { TranslationIDs } from '@capsicum/common';

type OnboardingTutorialStepProps = {
	header?: TranslationIDs;
	stepId: WCPledgeTutorialSteps;
	text: TranslationIDs;
	textParams?: Record<string, unknown>;
	tooltipText?: TranslationIDs;
};

export const useWCTutorials = () => {
	const { isUkUser } = useUkAwareText();
	const { formattedBonusPercentage } = useWCPledgeBonusPercentage();

	const intlText: Record<string, TranslationIDs> = useMemo(
		() => ({
			fifthTutorialText: 'worldChampionshipPledge.tutorial.fifthTutorialText',
			finalTutorialText: isUkUser
				? 'worldChampionshipPledge.tutorial.sixthTutorialTextUk'
				: 'worldChampionshipPledge.tutorial.sixthTutorialText',
			finalTutorialTitle: 'worldChampionshipPledge.tutorial.sixthTutorialTitle',
			firstTutorialText: 'worldChampionshipPledge.tutorial.firstTutorialText',
			firstTutorialTitle: 'worldChampionshipPledge.tutorial.firstTutorialTitle',
			fourthTutorialText: 'worldChampionshipPledge.tutorial.fourthTutorialText',
			header: 'worldChampionshipPledge.tutorial.header',
			secondTutorialText: isUkUser
				? 'worldChampionshipPledge.tutorial.secondTutorialTextUk'
				: 'worldChampionshipPledge.tutorial.secondTutorialText',
			tenExtraTutorialText: 'worldChampionshipPledge.tutorial.tenExtraTutorialText',
			tenExtraTutorialTooltip: 'worldChampionshipPledge.tutorial.tenExtraTutorialTooltip',
			thirdTutorialText: 'worldChampionshipPledge.tutorial.thirdTutorialText'
		}),
		[isUkUser]
	);

	const onboardingTutorialSteps = useMemo(() => {
		const optionalSteps: Array<OnboardingTutorialStepProps> = [
			{
				enable: !isUkUser,
				stepId: WCPledgeTutorialSteps.TenPercentExtra,
				text: intlText.tenExtraTutorialText!,
				tooltipText: intlText.tenExtraTutorialTooltip!
			}
		]
			.filter(step => step.enable)
			// eslint-disable-next-line unused-imports/no-unused-vars
			.map(({ enable, ...step }) => step);

		const stepsProps: Array<OnboardingTutorialStepProps> = [
			{
				header: intlText.firstTutorialTitle,
				stepId: WCPledgeTutorialSteps.First,
				text: intlText.firstTutorialText!
			},
			{
				stepId: WCPledgeTutorialSteps.Second,
				text: intlText.secondTutorialText!,
				textParams: { bonusPercentage: formattedBonusPercentage }
			},
			{ stepId: WCPledgeTutorialSteps.Third, text: intlText.thirdTutorialText! },
			...optionalSteps,
			{ stepId: WCPledgeTutorialSteps.Fourth, text: intlText.fourthTutorialText! },
			{ stepId: WCPledgeTutorialSteps.Fifth, text: intlText.fifthTutorialText! },
			{
				header: intlText.finalTutorialTitle,
				stepId: WCPledgeTutorialSteps.Final,
				text: intlText.finalTutorialText!
			}
		];

		return stepsProps;
	}, [isUkUser, formattedBonusPercentage, intlText]);

	return {
		intlText,
		onboardingTutorialSteps
	};
};
